import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Modal from 'react-modal';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UilCalendarAlt, UilTimes, UilPhone, UilImages,
  UilCheckCircle, UilArrowRight, UilSearchPlus,
} from '@iconscout/react-unicons';
import { storage } from '../firebase';
import { listAll, getDownloadURL, ref } from 'firebase/storage';
import LazyLoad from 'react-lazyload';
import CleanPlace from '../assets/Images/CleanPlace.jpg';
import FooterPage from '../components/FooterPage';

Modal.setAppElement('#root');

const HIGHLIGHTS = [
  { icon: <UilCheckCircle size={28} />, title: 'Real Results',    description: 'Authentic photos of our work', color: 'bg-brand-50 text-brand-600' },
  { icon: <UilImages size={28} />,      title: '100+ Projects',   description: 'Completed with excellence',    color: 'bg-amber-50 text-amber-600' },
  { icon: <UilCheckCircle size={28} />, title: 'Verified Quality', description: '5-star rated service',        color: 'bg-emerald-50 text-emerald-600' },
];

export default function Gallery() {
  const [images,   setImages]   = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    const storageRef = ref(storage, 'Images/');
    listAll(storageRef)
      .then((res) => Promise.all(res.items.map((item) => getDownloadURL(item))))
      .then((urls) => { setImages(urls); setLoading(false); })
      .catch((err) => { console.error(err); setLoading(false); });
  }, []);

  return (
    <div className="bg-white text-slate-900">
      <Helmet>
        <title>Gallery - Fabiana Fabulous Cleaning Services | Our Work</title>
        <meta name="description" content="View our professional cleaning work gallery. See before and after photos of home cleaning, office cleaning, and deep cleaning services in Seattle & Eastside." />
        <link rel="canonical" href="https://fabycleaners.com/gallery" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-end bg-cover bg-center" style={{ backgroundImage: `url(${CleanPlace})` }}>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-slate-900/20" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center text-white">
            <span className="inline-block bg-gold-500/20 border border-gold-400/30 text-gold-300 text-xs font-semibold px-4 py-2 rounded-full mb-5">
              View Our Professional Results
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-5 leading-tight">Our Work Gallery</h1>
            <p className="text-white/65 text-xl max-w-xl mx-auto mb-12">Experience the art of immaculate living through our portfolio.</p>
            <div className="flex flex-wrap justify-center gap-6">
              {HIGHLIGHTS.map((h) => (
                <div key={h.title} className="text-center">
                  <div className={`w-14 h-14 ${h.color} rounded-2xl flex items-center justify-center mx-auto mb-3`}>{h.icon}</div>
                  <p className="font-bold text-white text-sm">{h.title}</p>
                  <p className="text-white/50 text-xs mt-0.5">{h.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="inline-block bg-brand-50 text-brand-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">Portfolio</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Real Results From Real Clients</h2>
            <p className="text-slate-500">Click any photo to view full size.</p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="fresh-spinner" />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <UilImages size={36} className="text-slate-400" />
              </div>
              <p className="text-slate-500 font-medium">No images available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {images.map((url, i) => (
                <LazyLoad key={i} height={300} offset={100} once>
                  <motion.div
                    initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04, duration: 0.4 }}
                    className="group relative overflow-hidden rounded-2xl border border-slate-100 cursor-pointer shadow-card hover:shadow-card-hover transition-all"
                    onClick={() => setSelected(url)}
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-slate-50">
                      <img src={url}
                        alt={`Professional cleaning result ${i + 1}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy" />
                      <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/40 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-slate-900 rounded-xl px-4 py-2.5 flex items-center gap-2 text-sm font-semibold">
                          <UilSearchPlus size={18} /> View Full Size
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </LazyLoad>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selected && (
          <Modal isOpen onRequestClose={() => setSelected(null)}
            className="fixed inset-0 flex items-center justify-center p-4 outline-none z-50"
            overlayClassName="fixed inset-0 bg-slate-900/95 z-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.25 }}
              className="relative max-w-5xl w-full"
            >
              <img src={selected} alt="Enlarged cleaning result" className="w-full h-auto rounded-2xl shadow-2xl" />
              <button onClick={() => setSelected(null)}
                className="absolute -top-4 -right-4 w-10 h-10 bg-white hover:bg-slate-100 text-slate-900 rounded-full shadow-lg flex items-center justify-center transition-colors">
                <UilTimes size={20} />
              </button>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="w-16 h-16 bg-brand-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <UilCheckCircle size={32} className="text-white" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Ready for Results Like These?</h2>
            <p className="text-gold-400 text-lg font-semibold mb-2">Refer us and get 15% off your next cleaning!</p>
            <p className="text-slate-400 mb-10">Experience the same exceptional quality shown in our gallery.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:425-829-6092"
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold px-8 py-4 rounded-xl transition-colors">
                <UilPhone size={20} /> Call (425) 829-6092
              </a>
              <Link to="/request"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold px-8 py-4 rounded-xl transition-all">
                <UilCalendarAlt size={20} /> Get Free Quote <UilArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <FooterPage />
    </div>
  );
}
